// @givmo/auth — popup-first OAuth strategy with a same-site custom-authDomain redirect fallback.
//
// WHY (the spec's hard constraint, confirmed current in 03-kickoff-verifications-2026-06-24 §Firebase):
// `signInWithRedirect` is BROKEN-BY-DEFAULT on Chrome 115+ / Safari 16.1+ / Firefox 109+ for
// NON-same-site origins, because those browsers partition / block third-party storage and the
// redirect handshake bounces through Firebase's default `*.firebaseapp.com` authDomain — a
// cross-site origin relative to the app. So:
//
//   1. PRIMARY: `signInWithPopup`. The popup runs the OAuth handshake in a first-party-ish window and
//      doesn't depend on cross-site cookies the way the redirect bounce does. This is the default for
//      every provider on every app.
//   2. FALLBACK: `signInWithRedirect`, but ONLY when the app has been configured with a SAME-SITE
//      custom `authDomain` (e.g. `donate.givmo.io` serving the `/__/auth/handler` itself, instead of
//      `givmo-prod.firebaseapp.com`). With a same-site authDomain the redirect handshake is
//      first-party and works; without one it would silently fail, so we refuse to fall back.
//
// The popup is tried first; we only consider the redirect path when the popup is unavailable/blocked
// AND a same-site authDomain is present. A user-initiated popup cancel is surfaced as a normal
// cancellation (NOT auto-escalated to a redirect — escalating a deliberate cancel would re-prompt).
//
// This matters most for the Cloud Run-hosted RR8 SSR apps (donate/consumer/pay) and the 04 consent
// app (`connect.givmo.io`), which are exactly the cross-site cases the default authDomain breaks on.

import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  linkWithPopup,
  linkWithRedirect,
  type Auth,
  type AuthProvider,
  type User,
  type UserCredential,
} from 'firebase/auth';
import { AuthError } from './errors';
import { withSecondFactorChallenge } from './mfa';

/** How a same-site redirect fallback is configured. */
export interface PopupStrategyOptions {
  /**
   * The app's configured Firebase `authDomain`. The redirect fallback is permitted ONLY when this is
   * a SAME-SITE custom domain (i.e. equal to / a subdomain registrar-match of `window.location.host`),
   * because a cross-site `*.firebaseapp.com` redirect is the broken path. Omit (or leave it the
   * default `*.firebaseapp.com`) to disable the fallback — popup failures then surface as errors.
   */
  authDomain?: string;
  /**
   * Force a particular path. `'popup'` (default) tries popup first; `'redirect'` goes straight to the
   * redirect path (only valid with a same-site `authDomain`). Mostly for apps that have deliberately
   * chosen redirect (rare) or for tests.
   */
  prefer?: 'popup' | 'redirect';
}

/** True when `authDomain` is same-site relative to the current page (so a redirect handshake is first-party). */
export function isSameSiteAuthDomain(
  authDomain: string | undefined,
  host: string | undefined,
): boolean {
  if (!authDomain || !host) return false;
  // A default Firebase authDomain (`*.firebaseapp.com` / `*.web.app`) is cross-site → not eligible.
  if (authDomain.endsWith('.firebaseapp.com') || authDomain.endsWith('.web.app')) return false;
  const domain = authDomain.toLowerCase();
  const current = host.toLowerCase().split(':')[0] ?? '';
  // Same host, or the authDomain is a registrable suffix of the current host (sub.givmo.io vs givmo.io).
  return current === domain || current.endsWith(`.${domain}`) || domain.endsWith(`.${current}`);
}

/** True when the page can plausibly host a redirect fallback (browser context + eligible authDomain). */
export function canUseRedirectFallback(options: PopupStrategyOptions): boolean {
  if (typeof window === 'undefined') return false;
  return isSameSiteAuthDomain(options.authDomain, window.location?.host);
}

/** Popup failure codes that mean "the popup mechanism is unavailable" (eligible for redirect escalation). */
const POPUP_UNAVAILABLE_KINDS = new Set<AuthError['kind']>(['popupBlocked']);

/**
 * The shared popup-first → same-site-redirect-fallback driver. Both `signInWithStrategy` (a
 * brand-new sign-in) and `linkWithStrategy` (linking a provider onto an existing user) run the SAME
 * decision; only the underlying popup/redirect pair differs (`signInWith*` vs `linkWith*`). Keeping a
 * single driver guarantees the fallback condition — try popup, escalate to redirect ONLY on a BLOCKED
 * popup (`popupBlocked`) when a same-site custom `authDomain` is configured, never on a user cancel —
 * stays identical across the two flows rather than drifting into a second hand-rolled strategy.
 */
async function runPopupFirstStrategy(
  popup: () => Promise<UserCredential>,
  redirect: () => Promise<never>,
  options: PopupStrategyOptions,
): Promise<UserCredential> {
  if (options.prefer === 'redirect') {
    if (!canUseRedirectFallback(options)) {
      throw new AuthError('unknown', {
        message: 'Redirect sign-in requires a same-site custom authDomain; none is configured.',
      });
    }
    await redirect();
    // Page navigates; this promise never settles. The cast documents the unreachable return.
    return await new Promise<UserCredential>(() => {});
  }

  try {
    return await popup();
  } catch (rawError) {
    const error = AuthError.from(rawError);
    if (POPUP_UNAVAILABLE_KINDS.has(error.kind) && canUseRedirectFallback(options)) {
      await redirect();
      return await new Promise<UserCredential>(() => {});
    }
    throw error;
  }
}

/**
 * Run an OAuth sign-in with the popup-first strategy.
 *
 * Returns a `UserCredential` when the popup completes. If the popup is BLOCKED (not merely closed by
 * the user) and a same-site redirect fallback is available, this kicks off `signInWithRedirect` and
 * NEVER RESOLVES (the page navigates away) — the caller completes the flow on return via
 * `completeRedirectSignIn`. A user cancel (`popupClosedByUser` / `cancelled`) is rethrown as an
 * `AuthError` rather than escalated.
 *
 * An OAuth first factor on an account with an enrolled second factor rejects with a
 * `SecondFactorRequiredError` carrying the pending {@link TotpChallenge} (AS-WEB1). This is the one
 * seam EVERY app's provider sign-in shares — admin, charity, super-admin and consent all reach OAuth
 * through here — so handling it once is what keeps an enrolled admin from dead-ending anywhere.
 */
export function signInWithStrategy(
  auth: Auth,
  provider: AuthProvider,
  options: PopupStrategyOptions = {},
): Promise<UserCredential> {
  return runPopupFirstStrategy(
    () => signInWithPopup(auth, provider),
    () => signInWithRedirect(auth, provider),
    options,
  ).catch((error: unknown) => {
    throw withSecondFactorChallenge(auth, error);
  });
}

/**
 * Link an OAuth provider onto an EXISTING `user` with the SAME popup-first strategy as
 * `signInWithStrategy` — only the Firebase primitives differ (`linkWith*` instead of `signInWith*`).
 * The Firebase JS SDK does NOT auto-link on `signInWithPopup`/`Redirect` (those perform a fresh
 * sign-in and throw `account-exists-with-different-credential`); `linkWithPopup`/`linkWithRedirect`
 * are the real link primitives. Returns the merged `UserCredential` on a completed popup; a BLOCKED
 * popup escalates to `linkWithRedirect` (page navigates, never resolves — complete via
 * `completeRedirectSignIn`) only when a same-site custom `authDomain` is configured. A user cancel is
 * rethrown, not escalated.
 */
export function linkWithStrategy(
  user: User,
  provider: AuthProvider,
  options: PopupStrategyOptions = {},
): Promise<UserCredential> {
  return runPopupFirstStrategy(
    () => linkWithPopup(user, provider),
    () => linkWithRedirect(user, provider),
    options,
  );
}

/**
 * Complete a redirect-based sign-in on page load. Returns the `UserCredential` if the page is
 * returning from a redirect handshake, or `null` if there was no pending redirect. Normalises any
 * error to an `AuthError` — including the second-factor challenge, which the returning handshake
 * raises just as the popup path does. Call once at app boot when the redirect fallback is in play.
 */
export async function completeRedirectSignIn(auth: Auth): Promise<UserCredential | null> {
  try {
    return await getRedirectResult(auth);
  } catch (rawError) {
    throw withSecondFactorChallenge(auth, rawError);
  }
}
